import { isLeafletMapUsable, stopLeafletMap } from 'components/BaseMap/util/BaseMap.utils';
import { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';

const parseCenter = (value) => {
  const MAX_LAT = 85.05112878; // Clamp to Web Mercator limits to avoid infinite tile requests

  const clampLatLng = (lat, lng) => [
    Math.min(Math.max(lat, -MAX_LAT), MAX_LAT),
    Math.min(Math.max(lng, -180), 180),
  ];

  if (!value) return null;
  if (Array.isArray(value)) {
    const lat = Number.parseFloat(value[0]);
    const lng = Number.parseFloat(value[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? clampLatLng(lat, lng) : null;
  }

  if (typeof value === 'object') {
    const lat = Number.parseFloat(value.lat ?? value.latitude);
    const lng = Number.parseFloat(value.lng ?? value.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? clampLatLng(lat, lng) : null;
  }

  return null;
};

export default function LocationSetter({ center, zoom, preserveZoom = false }) {
  const map = useMap();
  const lastSyncKeyRef = useRef(null);

  const parsedCenter = useMemo(() => parseCenter(center), [center]);
  const explicitCenterZoom = useMemo(() => {
    if (Array.isArray(center)) {
      const z = Number(center[2]);
      return Number.isFinite(z) ? z : null;
    }
    if (center && typeof center === 'object') {
      const z = Number(center.zoom);
      return Number.isFinite(z) ? z : null;
    }
    return null;
  }, [center]);

  useEffect(() => {
    if (!map || !parsedCenter) return;
    if (!isLeafletMapUsable(map)) return;

    const zoomKey = preserveZoom ? 'preserve' : `${Number(zoom)}`;
    const syncKey = `${parsedCenter[0]}:${parsedCenter[1]}:${explicitCenterZoom ?? 'none'}:${zoomKey}`;
    if (syncKey === lastSyncKeyRef.current) return;

    const targetZoom =
      explicitCenterZoom ??
      (preserveZoom ? map.getZoom() : Number.isFinite(zoom) ? zoom : map.getZoom());

    stopLeafletMap(map); // stop any ongoing map animations before setting a new view
    map.setView(parsedCenter, targetZoom, { animate: false });
    lastSyncKeyRef.current = syncKey;
  }, [map, explicitCenterZoom, parsedCenter, preserveZoom, zoom]);

  return null;
}
